/**
 * Pure logic behind the scenario builder: matching a bank to the captured
 * traffic that should drive it, generating an OPC UA script from the values an
 * adapter reacts to, and checking a scenario before it is saved.
 *
 * Everything here works on the generated Object API (`Simulation*T`), which is
 * also what the builder keeps as its state and what is sent to LiftCloud.
 */

import { AdapterProtocol } from "../../flatbuffers/generated/archive/adapter-protocol";
import type { SimulationAssetT } from "../../flatbuffers/generated/api/simulation-asset";
import { SimulationAssetKind } from "../../flatbuffers/generated/api/simulation-asset-kind";
import { SimulationBankSource } from "../../flatbuffers/generated/api/simulation-bank-source";
import type { SimulationCaptureFlowT } from "../../flatbuffers/generated/api/simulation-capture-flow";
import { SimulationCaptureReplayT } from "../../flatbuffers/generated/api/simulation-capture-replay";
import { SimulationOpcUaScriptT } from "../../flatbuffers/generated/api/simulation-opc-ua-script";
import { SimulationOpcUaStepT } from "../../flatbuffers/generated/api/simulation-opc-ua-step";
import type { SimulationScenarioT } from "../../flatbuffers/generated/api/simulation-scenario";
import { SimulationScenarioBankT } from "../../flatbuffers/generated/api/simulation-scenario-bank";
import type { SimulationTargetT } from "../../flatbuffers/generated/api/simulation-target";
import { uuidsEqual } from "../../utils/uuid";

/** Gap between two generated OPC UA writes, in milliseconds. */
export const DEFAULT_SCRIPT_STEP_MS = 5_000;

/** The longest captured silence a replay waits out before sending on, in milliseconds. */
export const DEFAULT_MAX_GAP_MS = 60_000;

const text = (value: string | Uint8Array | null | undefined): string => String(value ?? "");

/**
 * The `host:port` a sandbox tunnels for a bank's vendor address: the address
 * itself for TCP vendors, and the authority of an `opc.tcp://host:port/path`
 * endpoint URL for OPC UA, which is how `sandboxctl` lists it.
 */
export function tunnelAddressOf(vendorAddress: string): string {
  const match = /^opc\.tcp:\/\/([^/]+)/i.exec(vendorAddress.trim());
  return match ? match[1] : vendorAddress.trim();
}

/** Whether a sandbox tunnelling `tunnelled` can reach the bank at `vendorAddress`. */
export function sandboxReaches(tunnelled: readonly string[], vendorAddress: string): boolean {
  const address = tunnelAddressOf(vendorAddress);
  return address !== "" && tunnelled.includes(address);
}

/** A capture flow together with the asset it was found in. */
export interface CaptureMatch {
  asset: SimulationAssetT;
  flow: SimulationCaptureFlowT;
}

/**
 * The captured endpoint that should drive `target`.
 *
 * An endpoint captured at the bank's own vendor address wins: that is the
 * controller the channel points at. Otherwise the one endpoint whose detected
 * protocol is the bank's protocol is used, but only when exactly one such
 * endpoint exists across the library; with several candidates the engineer
 * chooses, so no guess is made.
 */
export function matchCaptureFlow(
  target: SimulationTargetT,
  assets: readonly SimulationAssetT[],
): CaptureMatch | null {
  const captures = assets.filter((asset) => asset.kind === SimulationAssetKind.CAPTURE);
  const address = text(target.vendorAddress);
  const atAddress: CaptureMatch[] = address
    ? captures.flatMap((asset) =>
        asset.flows
          .filter((flow) => text(flow.vendorAddress) === address)
          .map((flow) => ({ asset, flow })),
      )
    : [];
  if (atAddress.length > 0) return atAddress[0];
  const sameProtocol: CaptureMatch[] = captures.flatMap((asset) =>
    asset.flows
      .filter((flow) => flow.protocolDetected && flow.detectedProtocol === target.protocol)
      .map((flow) => ({ asset, flow })),
  );
  return sameProtocol.length === 1 ? sameProtocol[0] : null;
}

/** The library's OPC UA domain assets. */
export function opcUaDomains(assets: readonly SimulationAssetT[]): SimulationAssetT[] {
  return assets.filter((asset) => asset.kind === SimulationAssetKind.OPCUA_DOMAIN);
}

/**
 * An OPC UA script that walks every value the bank's adapter mapping reacts
 * to: one write every `stepMs`, node by node and value by value, looping.
 * Replaying it makes VTS decode every message the mapping can produce.
 */
export function autoScript(
  target: SimulationTargetT,
  domainAssetId: SimulationAssetT["id"],
  stepMs: number = DEFAULT_SCRIPT_STEP_MS,
): SimulationOpcUaScriptT {
  const script = new SimulationOpcUaScriptT();
  script.domainAssetId = domainAssetId;
  script.loop = true;
  let at = 0;
  target.opcuaNodes.forEach((node) => {
    node.values.forEach((value) => {
      script.steps.push(new SimulationOpcUaStepT(at, text(node.nodeId), value));
      at += stepMs;
    });
  });
  script.periodMs = at;
  return script;
}

/** A capture replay of `match` with the builder's defaults. */
export function captureReplayFor(match: CaptureMatch): SimulationCaptureReplayT {
  const replay = new SimulationCaptureReplayT();
  replay.assetId = match.asset.id;
  replay.flowVendorAddress = text(match.flow.vendorAddress);
  replay.maxGapMs = DEFAULT_MAX_GAP_MS;
  return replay;
}

/**
 * The scenario bank `target` starts as when it is added: an OPC UA bank gets
 * an auto script over the first OPC UA domain in the library, any other bank
 * the capture replay `matchCaptureFlow` finds. A bank with nothing to match
 * starts without a source and must be completed by hand.
 */
export function bankFromTarget(
  target: SimulationTargetT,
  assets: readonly SimulationAssetT[],
): SimulationScenarioBankT {
  const bank = new SimulationScenarioBankT();
  bank.bankId = target.bankId;
  bank.bankName = text(target.bankName);
  bank.protocol = target.protocol;
  bank.vendorAddress = text(target.vendorAddress);
  bank.enabled = true;
  if (target.protocol === AdapterProtocol.OPCUA) {
    const [domain] = opcUaDomains(assets);
    if (domain) {
      bank.sourceType = SimulationBankSource.SimulationOpcUaScript;
      bank.source = autoScript(target, domain.id);
    }
    return bank;
  }
  const match = matchCaptureFlow(target, assets);
  if (match) {
    bank.sourceType = SimulationBankSource.SimulationCaptureReplay;
    bank.source = captureReplayFor(match);
  }
  return bank;
}

/** The data fields of a generated Object API value, without its methods. */
type Fields<T> = Partial<Omit<T, "pack" | "clone">>;

/** A copy of `scenario` with `patch` applied; the builder's state is never edited in place. */
export function patchScenario(
  scenario: SimulationScenarioT,
  patch: Fields<SimulationScenarioT>,
): SimulationScenarioT {
  return Object.assign(scenario.clone(), patch);
}

/** A copy of `bank` with `patch` applied. */
export function patchBank(
  bank: SimulationScenarioBankT,
  patch: Fields<SimulationScenarioBankT>,
): SimulationScenarioBankT {
  return Object.assign(bank.clone(), patch);
}

/** A copy of `bank` replaying a capture, with `patch` applied to its replay settings. */
export function patchReplay(
  bank: SimulationScenarioBankT,
  patch: Fields<SimulationCaptureReplayT>,
): SimulationScenarioBankT {
  const current =
    bank.sourceType === SimulationBankSource.SimulationCaptureReplay
      ? (bank.source as SimulationCaptureReplayT | null)
      : null;
  const replay = Object.assign(current?.clone() ?? new SimulationCaptureReplayT(), patch);
  return patchBank(bank, {
    sourceType: SimulationBankSource.SimulationCaptureReplay,
    source: replay,
  });
}

/** A copy of `bank` running an OPC UA script, with `patch` applied to the script. */
export function patchScript(
  bank: SimulationScenarioBankT,
  patch: Fields<SimulationOpcUaScriptT>,
): SimulationScenarioBankT {
  const current =
    bank.sourceType === SimulationBankSource.SimulationOpcUaScript
      ? (bank.source as SimulationOpcUaScriptT | null)
      : null;
  const script = Object.assign(current?.clone() ?? new SimulationOpcUaScriptT(), patch);
  return patchBank(bank, {
    sourceType: SimulationBankSource.SimulationOpcUaScript,
    source: script,
  });
}

/** A copy of `scenario` in which the bank `bankId` is replaced by `change(bank)`. */
export function replaceBank(
  scenario: SimulationScenarioT,
  bankId: SimulationScenarioBankT["bankId"],
  change: (bank: SimulationScenarioBankT) => SimulationScenarioBankT,
): SimulationScenarioT {
  return patchScenario(scenario, {
    banks: scenario.banks.map((bank) => (uuidsEqual(bank.bankId, bankId) ? change(bank) : bank)),
  });
}

/**
 * A copy of `scenario` that does or does not drive `target`. Including a
 * bank for the first time adds it pre-matched (`bankFromTarget`); excluding
 * one keeps its settings, disabled, so ticking it again restores them.
 */
export function includeBank(
  scenario: SimulationScenarioT,
  target: SimulationTargetT,
  assets: readonly SimulationAssetT[],
  include: boolean,
): SimulationScenarioT {
  const existing = scenario.banks.find((bank) => uuidsEqual(bank.bankId, target.bankId));
  if (existing) {
    return replaceBank(scenario, target.bankId, (bank) => patchBank(bank, { enabled: include }));
  }
  if (!include) return scenario;
  return patchScenario(scenario, { banks: [...scenario.banks, bankFromTarget(target, assets)] });
}

/** The keys of the problems `scenarioProblems` can report, for translation. */
export type ScenarioProblem =
  | { key: "simulation.problemName" }
  | { key: "simulation.problemSandbox" }
  | { key: "simulation.problemNoBanks" }
  | { key: "simulation.problemNoSource"; bank: string }
  | { key: "simulation.problemNoFlow"; bank: string }
  | { key: "simulation.problemNoSteps"; bank: string };

/**
 * What stops `scenario` from being saved, in the order the builder shows its
 * fields; empty when it is ready. The server applies its own checks too (bank
 * ownership, asset kinds); these are the ones a field engineer can fix on the
 * spot.
 */
export function scenarioProblems(scenario: SimulationScenarioT): ScenarioProblem[] {
  const problems: ScenarioProblem[] = [];
  if (!text(scenario.name).trim()) problems.push({ key: "simulation.problemName" });
  if (!text(scenario.sandbox)) problems.push({ key: "simulation.problemSandbox" });
  const enabled = scenario.banks.filter((bank) => bank.enabled);
  if (enabled.length === 0) problems.push({ key: "simulation.problemNoBanks" });
  enabled.forEach((bank) => {
    const name = text(bank.bankName);
    if (bank.sourceType === SimulationBankSource.SimulationCaptureReplay) {
      const replay = bank.source as SimulationCaptureReplayT | null;
      if (!replay?.assetId || !text(replay.flowVendorAddress)) {
        problems.push({ key: "simulation.problemNoFlow", bank: name });
      }
    } else if (bank.sourceType === SimulationBankSource.SimulationOpcUaScript) {
      const script = bank.source as SimulationOpcUaScriptT | null;
      if (!script?.domainAssetId || script.steps.length === 0) {
        problems.push({ key: "simulation.problemNoSteps", bank: name });
      }
    } else {
      problems.push({ key: "simulation.problemNoSource", bank: name });
    }
  });
  return problems;
}
